/**
 * rackPlacement — cuentas de ocupación de un rack, sin React.
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * El visor coloca equipos por dos caminos: arrastrar y soltar (ratón) y, desde
 * el «modo tablet», tocar el equipo y luego el hueco. Los dos tienen que decir
 * exactamente lo mismo sobre qué U están libres; si la cuenta vive copiada en
 * cada handler, un día una permite lo que la otra prohíbe y el backend devuelve
 * un 400 por solape.
 *
 * CONVENIO DE ALTURAS (el del CMDB, no lo cambies)
 * ---------------------------------------------------------------------------
 * `position_u` es la **U inferior** del equipo; un equipo de 2U en U10 ocupa
 * U10 y U11, y su **U superior** (la que se ve primero al mirar el rack de
 * arriba abajo) es U11. En el visor, un slot se pinta en la U SUPERIOR y crece
 * hacia abajo. `topU` es siempre esa U superior.
 */

/** Rango inclusivo de un equipo: `bottom` = position_u, `top` = position_u + tamaño − 1. */
export function rangeOf(startU, sizeU = 1) {
  if (!startU) return null;
  const len = Math.max(1, Number(sizeU) || 1);
  return { bottom: Number(startU), top: Number(startU) + len - 1 };
}

/** ¿Se pisan dos rangos? Cualquier extremo `null` no se considera solape. */
export function rangesOverlap(a, b) {
  if (!a || !b) return false;
  return !(a.bottom > b.top || b.bottom > a.top);
}

/** U superior (la del slot donde se pinta) de un elemento del rack. */
export function topUOf(el) {
  const start = el?.position_u || el?.position;
  if (!start) return null;
  const size = el?.size_u || el?.size || 1;
  return Number(start) + Number(size) - 1;
}

/** Todas las U que ocupa un elemento colocado. */
export function elementUs(el) {
  const range = rangeOf(el?.position_u || el?.position, el?.size_u || el?.size || 1);
  if (!range) return [];
  const out = [];
  for (let u = range.bottom; u <= range.top; u += 1) out.push(u);
  return out;
}

/**
 * ¿Cabe un equipo cuya U SUPERIOR sería `topU`?
 *
 * @param {object}   args
 * @param {number}   args.topU       U superior donde quedaría el equipo.
 * @param {number}   args.sizeU      Altura en U.
 * @param {number}   args.heightU    Altura total del rack.
 * @param {Set<number>} args.occupied U ocupadas en esa cara (equipos + tapas +
 *                                    bloqueo por profundidad completa del otro lado).
 * @param {Set<number>} [args.blocked] U bloqueadas además de las ocupadas (se
 *                                    suma a `occupied`; existe por claridad).
 * @param {number[]} [args.ignore]    U del propio equipo cuando se está moviendo
 *                                    dentro de la misma cara: no cuentan como
 *                                    ocupadas por sí mismo.
 */
export function canPlaceAt({ topU, sizeU = 1, heightU, occupied, blocked, ignore = [] }) {
  const size = Math.max(1, Number(sizeU) || 1);
  const top = Number(topU);
  if (!Number.isFinite(top) || top < 1 || top > heightU) return false;
  const bottom = top - size + 1;
  if (bottom < 1) return false;

  const skip = new Set(ignore || []);
  for (let u = bottom; u <= top; u += 1) {
    // Una U bloqueada por profundidad completa del otro lado nunca es válida,
    // ni siquiera para el equipo que estamos moviendo.
    if (blocked?.has(u)) return false;
    if (skip.has(u)) continue;
    if (occupied?.has(u)) return false;
  }
  return true;
}
